/* eslint-disable prettier/prettier */
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import {
  IAttendant,
  IEventAndSessionData,
  IGetDuplicatesParams,
} from "../../interfaces/reports.interfaces";
import { ICatalogItem } from "src/app/catalog/interfaces/catalog.interface";
import { AssistanceService } from "src/app/attendanceRecord/services/asistanceService/assistance.service";
import { ModalSharedServiceService } from "src/app/shared/modals/modal-service.service";
import { ResponseDataInterfaces } from "src/app/models/Interfaces/ResponseDataInterfaces";
import { SessionsService } from "src/app/services/session/sessions.service";
import { CatalogService } from "src/app/catalog/services/catalog.service";
import { Subject, takeUntil } from "rxjs";
import { OffCanvasComponent } from "src/app/shared/off-canvas/off-canvas.component";
import { IOffCanvasOptions } from "src/app/shared/off-canvas/off-canvas.interface";

@Component({
  selector: "app-duplicate-attendees-table",
  templateUrl: "./duplicate-attendees-table.component.html",
  styleUrls: ["./duplicate-attendees-table.component.css"],
})
export class DuplicateAttendeesTableComponent implements OnInit, OnDestroy {
  // output
  @Output()
  public closeModalEmitter: EventEmitter<boolean> = new EventEmitter();

  // input
  @Input()
  public eventAndSessionData?: IEventAndSessionData;

  @Input()
  public incompleteAttendance: boolean = false;

  // offcanvas
  @ViewChild("offcanvasComponent") offcanvasComponent!: OffCanvasComponent;
  protected offCanvasOptions: IOffCanvasOptions = {
    title: "Detalles",
    ngbOptions: {
      keyboard: true,
      position: "end",
      container: ".modal",
      beforeDismiss: () => {
        this.selectedAttendant = undefined;
        this.offCanvasOptions.title = "Detalles";
        return true;
      },
    },
  };
  public selectedAttendant?: IAttendant;

  // data
  public duplicateAttendees: IAttendant[] = [];

  // flags
  protected isLoadingDuplicates: boolean = false;
  protected errorOccurred: boolean = false;
  protected isLoadingConfirmBtn: boolean = false;

  // table
  public selectedAttendees = new Set<string>();
  public generalJustification: number | null = -1; //  initial value -1 to avoid a default option
  protected pageSize: number = 10;
  protected page: number = 1;

  // catalogs
  private unsubscribe$ = new Subject<void>();
  public justificationList: ICatalogItem[] = [];

  constructor(
    private assistanceService: AssistanceService,
    private sessionsService: SessionsService,
    private catalogService: CatalogService,
    private modalService: ModalSharedServiceService
  ) {}

  ngOnInit(): void {
    this.subscribeToJustificationCatalog();
    this.getDuplicates();
    this.updateOffCanvasResponsive();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  // getters
  get collectionSize() {
    return this.duplicateAttendees.length;
  }
  get noDuplicatesConfirmMsg() {
    if (this.incompleteAttendance) {
      return "La sesión sólo se registrará para los participantes con asistencias completas. Si deseas registrar a los faltantes, completa el total de sus asistencias y envía de nuevo la sesión.";
    }
    return "Las asistencias se enviaran a GB University";
  }
  get noDuplicatesConfirmTitle() {
    if (this.incompleteAttendance) {
      return "Advertencia: participantes incompletos";
    }
    return "";
  }

 @HostListener("window:resize")
  onResize() {
    this.updateOffCanvasResponsive();
  }

  updateOffCanvasResponsive() {
    const currentOptions = this.offCanvasOptions.ngbOptions ?? {};
    if (window.innerWidth < 768) {
      this.offCanvasOptions.ngbOptions = {
        ...currentOptions,
        position: "bottom",
        panelClass: "long-panel",
      };
    } else {
      this.offCanvasOptions.ngbOptions = {
        ...currentOptions,
        panelClass: "wide-panel",
        position: "end",
      };
    }
  }

  private subscribeToJustificationCatalog() {
    this.catalogService.justificationCatalogList
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(list => {
        this.isLoadingDuplicates = false;
        this.justificationList = list;
      });
  }

  getDuplicates() {
    if (!this.eventAndSessionData) {
      return;
    }
    this.isLoadingDuplicates = true;
    const params: IGetDuplicatesParams = {
      localizadorId: this.eventAndSessionData.locatorId,
      yearId: this.eventAndSessionData.yearId,
    };

    this.assistanceService
      .getDuplicateAttendees(this.eventAndSessionData.fkObjectIdExt, params)
      .subscribe({
        next: duplicates => {
          this.duplicateAttendees = duplicates;
          this.errorOccurred = false;
          if (duplicates.length === 0) {
            this.isLoadingDuplicates = false;
            return;
          }
          this.catalogService.getCatalogList("justification").catch(() => {
            this.isLoadingDuplicates = false;
            this.errorOccurred = true;
          });
        },
        error: () => {
          this.errorOccurred = true;
          this.isLoadingDuplicates = false;
        },
      });
  }

  getClassByJustification(justificationId: number | null) {
    switch (justificationId) {
      case -1:
        return "neutral-row";
      case null:
        return "delete-row";

      default:
        return "justification-row";
    }
  }

  // checkbox and multiple select logic
  toggleSelection(numeroPersona: string): void {
    this.generalJustification = -1;
    if (this.selectedAttendees.has(numeroPersona)) {
      this.selectedAttendees.delete(numeroPersona);
    } else {
      this.selectedAttendees.add(numeroPersona);
    }
  }

  toggleSelectAll(event: any): void {
    const checked = event.target.checked;

    if (checked) {
      this.duplicateAttendees.forEach(attendee =>
        this.selectedAttendees.add(attendee.numeroPersona)
      );
    } else {
      this.generalJustification = -1;
      this.selectedAttendees.clear();
    }
  }

  isAllSelected(): boolean {
    return (
      this.duplicateAttendees.length > 0 &&
      this.duplicateAttendees.every(attendee =>
        this.selectedAttendees.has(attendee.numeroPersona)
      )
    );
  }

  applyJustificationToSelected(): void {
    this.duplicateAttendees.forEach(attendee => {
      if (this.selectedAttendees.has(attendee.numeroPersona)) {
        attendee.fkIdJustificante = this.generalJustification;
      }
    });
  }

  getFullName(attendant: IAttendant) {
    return `${attendant.nombre ?? ""} ${attendant.segundoNombre ?? ""} ${
      attendant.apellidoPaterno ?? ""
    } ${attendant.apellidoMaterno ?? ""}`;
  }

  async onContinue() {
    this.isLoadingConfirmBtn = true;
    if (this.collectionSize === 0) {
      this.queueAttendance(false);
      return;
    }
    await this.updateDuplicateAttendees();
  }

  queueAttendance(fromUpdateDuplicates: boolean) {
    if (!this.eventAndSessionData) {
      return;
    }
    this.isLoadingConfirmBtn = true;
    const successMessage = `${
      fromUpdateDuplicates ? "Se han actualizado los duplicados y ha" : "Ha"
    } comenzado el proceso de integrar las asistencias a GB University.`;
    this.sessionsService
      .AddQueueAssistance(
        this.eventAndSessionData?.locatorId,
        this.eventAndSessionData?.locatorId
      )
      .subscribe({
        next: () => {
          this.modalService
            .openModal("success-modal", {
              title: "¡Éxito!",
              body: successMessage,
            })
            .then(() => {
              this.isLoadingConfirmBtn = false;
              this.closePopUp(true);
            });
        },
        error: error => {
          const personalizedError = error.error as ResponseDataInterfaces<null>;
          let msg = `Ha ocurrido un error al integrar las asistencias a GB University, Inténtalo mas tarde.`;

          if (
            error.status === 422 &&
            Number(personalizedError.statusCode) === 422
          ) {
            msg = personalizedError.message;
          }
          this.modalService
            .openModal("error-modal", {
              title: "¡Error!",
              body: `${
                fromUpdateDuplicates ? "Se actualizaron los duplicados." : ""
              } ${msg}`,
            })
            .then(() => {
              this.isLoadingConfirmBtn = false;
              this.closePopUp(fromUpdateDuplicates);
            });
        },
      });
  }

  async updateDuplicateAttendees() {
    const confirmation = await this.modalService.openModal("confirm-modal", {
      title: `Confirmar acción. ${this.noDuplicatesConfirmTitle}`,
      body: `¿Estás seguro de que deseas eliminar/justificar a los asistentes duplicados y registrar las asistencias en GBU? ${
        this.incompleteAttendance ? this.noDuplicatesConfirmMsg : ""
      }`,
    });
    if (!confirmation) {
      this.isLoadingConfirmBtn = false;
      return;
    }
    this.assistanceService
      .updateDuplicateAttendees(this.duplicateAttendees)
      .subscribe({
        next: () => {
          this.queueAttendance(true);
        },
        error: error => {
          const personalizedError = error.error as ResponseDataInterfaces<null>;
          let msg = `Error al actualizar duplicados, Inténtalo mas tarde.`;

          if (
            error.status === 422 &&
            Number(personalizedError.statusCode) === 422
          ) {
            msg = personalizedError.message;
          }
          this.modalService
            .openModal("error-modal", {
              title: "¡Error!",
              body: msg,
            })
            .then(() => {
              this.isLoadingConfirmBtn = false;
            });
        },
      });
  }

  openOffcanvas(attendant: IAttendant) {
    this.offCanvasOptions.title = `${this.getFullName(attendant)}`;
    this.selectedAttendant = attendant;
    this.offcanvasComponent.open();
  }

  closeOffCanvas() {
    this.selectedAttendant = undefined;
    this.offCanvasOptions.title = "Detalles";
    this.offcanvasComponent.close();
  }

  // popup
  closePopUp(isSuccess: boolean) {
    this.closeModalEmitter.emit(isSuccess);
  }
}
